import { ColDef } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import ActionsColumn from 'components/tables/data/user/columns/ActionsColumn';
import CertificatesColumn from 'components/tables/data/user/columns/CertificatesColumn';
import FullNameColumn from 'components/tables/data/user/columns/FullNameColumn';
import MigratedFromV1NoteColumn from 'components/tables/data/user/columns/MigratedFromV1NoteColumn';
import OrganizationNameColumn from 'components/tables/data/user/columns/OrganizationNameColumn';
import PermissionsColumn from 'components/tables/data/user/columns/PermissionsSummaryColumn';
import PasswordValidUntilColumn from 'components/tables/data/user/columns/PasswordValidUntilColumn';
import RoleNameColumn from 'components/tables/data/user/columns/RoleNameColumn';
import SignatureColumn from 'components/tables/data/user/columns/SignatureColumn';
import UserIdColumn from 'components/tables/data/user/columns/UserIdColumn';
import { UserRow } from 'components/tables/data/user/types';
import DataTable, { IDataTablePublic } from 'components/tables/DataTable';
import CertificatesSelectFilter from 'components/tables/filters/CertificatesSelectFilter';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import RoleSelectFilter from 'components/tables/filters/RoleSelectFilter';
import UserPermissionsFilter, { UserPermissionsColumnFilter } from 'components/tables/filters/UserPermissionsFilter';
import { getDeviceType } from 'model/DeviceType';
import { ROLE_ADMIN_ID } from 'model/Role';
import { forwardRef, useCallback, useMemo } from 'react';
import { ADD_USER_PAGE_PATH } from 'routes/paths';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';
import { FEATURE_TOGGLE_SIGNATURE_IN_USERS_TABLE } from 'env';

interface UserTableProps {
  suppressSaveProfiles?: boolean;
  suppressProfileChangedNotification?: boolean;
  initialFilters?: Record<string, any>;
}

/**
 * Table which displays users.
 */
const UserTable = forwardRef<IDataTablePublic, UserTableProps>(
  ({ suppressSaveProfiles = false, suppressProfileChangedNotification = false, initialFilters }, ref) => {
    const { getAction, roleId } = useApi();
    const { tableActionMode } = useResponsiveDesign();

    const columns = useMemo(() => {
      const columns: ColDef[] = [];

      columns.push({
        field: 'userId',
        headerName: 'ID',
        sortable: true,
        unSortIcon: true,
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        cellRenderer: UserIdColumn,
      });

      if (FEATURE_TOGGLE_SIGNATURE_IN_USERS_TABLE) {
        columns.push({
          field: 'signatureFileId',
          headerName: 'Podpis',
          minWidth: 80,
          maxWidth: 80,
          width: 80,
          sortable: false,
          cellRenderer: SignatureColumn,
        });
      }

      columns.push({
        field: 'fullName',
        headerName: 'Meno',
        minWidth: 350,
        sortable: true,
        unSortIcon: true,
        cellRenderer: FullNameColumn,
      });

      columns.push({
        field: 'preNominals',
        headerName: 'Titul pred menom',
        hide: true,
      });

      columns.push({
        field: 'postNominals',
        headerName: 'Titul za menom',
        hide: true,
      });

      columns.push({
        field: 'email',
        headerName: 'E-mail',
        filter: true,
        hide: true,
      });

      columns.push({
        field: 'phoneNumber',
        headerName: 'Telefónne číslo',
        filter: true,
        hide: true,
      });

      columns.push({
        valueGetter: ({ data: { status } }: { data: UserRow }) => (status ? '1' : '0'),
        field: 'status',
        headerName: 'Aktívny',
        filter: true,
        hide: true,
      });

      columns.push({
        field: 'organization.organizationId',
        headerName: 'ID organizácie',
        hide: true,
        filter: OrganizationMultiColumnFilter,
      });

      columns.push({
        field: 'organization.organizationName',
        headerName: 'Organizácia',
        minWidth: 250,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: OrganizationMultiSelectFilter.configure({ permissionSlug: 'manage-users' }),
        sortable: true,
        unSortIcon: true,
        cellRenderer: OrganizationNameColumn,
      });

      if (roleId === ROLE_ADMIN_ID) {
        columns.push({
          field: 'role.roleId',
          headerName: 'ID roly',
          hide: true,
          filter: true,
        });

        columns.push({
          field: 'role.roleName',
          headerName: 'Rola',
          minWidth: 150,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: RoleSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: RoleNameColumn,
        });

        columns.push({
          field: 'passwordValidUntil',
          headerName: 'Platnosť hesla do',
          minWidth: 220,
          sortable: true,
          unSortIcon: true,
          cellRenderer: PasswordValidUntilColumn,
        });
      }

      columns.push({
        field: 'certificateSlugs',
        hide: true,
        filter: true,
      });

      columns.push({
        field: 'certificates',
        headerName: 'Osvedčenia',
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: CertificatesSelectFilter,
        minWidth: 240,
        cellRenderer: CertificatesColumn,
      });

      columns.push({
        field: 'permissions',
        headerName: 'Oprávenia',
        minWidth: 400,
        filter: UserPermissionsColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: UserPermissionsFilter,
        cellRenderer: PermissionsColumn,
      });

      columns.push({
        field: 'migratedFromV1Note',
        headerName: 'Poznámka k migrácii',
        minWidth: 350,
        sortable: true,
        unSortIcon: true,
        cellRenderer: MigratedFromV1NoteColumn,
      });

      columns.push({
        field: '_actions',
        headerName: '',
        pinned: 'right',
        width: getResponsiveActionsColumnWidth(194, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        cellRenderer: ActionsColumn,
      });

      return columns;
    }, [roleId, tableActionMode]);

    const action = useCallback(async () => {
      const action = getAction('UserList');

      const users = await action();

      return users.map((user) => ({
        ...user,
        certificateSlugs: user.certificates.map(
          ({ deviceType, deviceTypeId }) => deviceType?.slug || getDeviceType(deviceTypeId)?.slug
        ),
      }));
    }, [getAction]);

    const columnKeys = useMemo(() => {
      const columns: string[] = [];

      columns.push('userId');
      columns.push('fullName');
      columns.push('preNominals');
      columns.push('postNominals');
      columns.push('email');
      columns.push('phoneNumber');
      columns.push('status');
      columns.push('migratedFromV1Note');
      columns.push('organization.organizationId');
      columns.push('organization.organizationName');

      if (roleId === ROLE_ADMIN_ID) {
        columns.push('role.roleId');
        columns.push('role.roleName');
        columns.push('passwordValidUntil');
      }

      return columns;
    }, [roleId]);

    return (
      <DataTable
        ref={ref}
        tableId="users"
        title="Používatelia"
        toggleDiscardedLabel="Zobraziť vyradených"
        addButtonText="Pridať nového"
        columns={columns}
        action={action}
        addButtonTarget={ADD_USER_PAGE_PATH.original}
        initialFilters={initialFilters}
        suppressSaveProfiles={suppressSaveProfiles}
        suppressProfileChangedNotification={suppressProfileChangedNotification}
        dataExport={{
          modalTitle: 'Exportovať používateľov',
          fileName: 'pouzivatelia.xlsx',
          columnKeys,
        }}
      />
    );
  }
);

export default UserTable;
